import { useEffect, useState } from "react";
import {
  CheckCircle2, ChevronDown, Copy, Flame, Inbox, Linkedin, Instagram, Mail, Plus,
  Settings2, Sparkles, Target, Trash2, X, XCircle,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const CHANNEL_META = {
  email: { label: "Email", Icon: Mail, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  linkedin: { label: "LinkedIn", Icon: Linkedin, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  instagram: { label: "Instagram", Icon: Instagram, cls: "bg-pink-50 text-pink-600 border-pink-200" },
};
const MODE_LABELS = [["manuel", "Manuel — suggestions uniquement"], ["validation", "Validation — l'humain approuve avant envoi (recommandé)"], ["automatique", "Automatique — canaux autorisés uniquement"]];
const CFG_FIELDS = [
  ["presentation", "Présentation de DGA", "textarea"], ["services", "Services proposés", "textarea"],
  ["ton", "Ton commercial", "input"], ["valeur", "Proposition de valeur", "textarea"],
  ["cibles", "Types de clients ciblés", "input"], ["zone", "Zone géographique", "input"],
  ["arguments", "Arguments commerciaux", "textarea"], ["offres", "Offres en cours", "input"],
  ["signature", "Signature email", "textarea"], ["horaires", "Horaires d'envoi", "input"],
];

export default function AgentIaPanel({ onClose, onChanged }) {
  const [summary, setSummary] = useState(null);
  const [queue, setQueue] = useState([]);
  const [config, setConfig] = useState(null);
  const [targets, setTargets] = useState([]);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [edited, setEdited] = useState({});
  const [schedFor, setSchedFor] = useState(null);
  const [busy, setBusy] = useState("");
  const [newTarget, setNewTarget] = useState(null);

  const load = () => {
    api.get("/prospection/agent/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/prospection/queue").then((r) => setQueue(r.data || [])).catch(() => {});
    api.get("/prospection/agent/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/prospection/agent/targets").then((r) => setTargets(r.data || [])).catch(() => {});
  };
  useEffect(load, []);

  const generate = async (a) => {
    setBusy(a.id);
    try {
      const { data } = await api.post(`/prospection/queue/${a.id}/generate`);
      setQueue((arr) => arr.map((x) => (x.id === a.id ? data : x)));
      toast.success("Message généré — relisez avant validation");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };
  const validate = async (a) => {
    setBusy(a.id);
    try {
      const content = edited[a.id]?.content ?? a.content;
      const subject = edited[a.id]?.subject ?? a.subject;
      await api.post(`/prospection/queue/${a.id}/validate`, { content, subject });
      toast.success(a.channel === "email" ? "Email envoyé au prospect" : "Tâche d'envoi manuel créée dans la To Do");
      load(); onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };
  const ignore = async (a) => {
    try {
      await api.post(`/prospection/queue/${a.id}/ignore`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveConfig = async () => {
    try {
      await api.put("/prospection/agent/config", config);
      toast.success("Configuration de l'agent enregistrée");
    } catch (e) { toast.error(apiError(e)); }
  };
  const addTarget = async () => {
    if (!newTarget?.name?.trim()) { toast.error("Nom de la cible requis"); return; }
    try {
      await api.post("/prospection/agent/targets", newTarget);
      setNewTarget(null);
      toast.success("Cible ajoutée");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delTarget = async (t) => {
    await api.delete(`/prospection/agent/targets/${t.id}`).catch(() => {});
    load();
  };

  const stats = summary ? [
    { label: "Prospects analysés", value: summary.analyzed_today, icon: Sparkles },
    { label: "Messages à valider", value: summary.a_valider, icon: Inbox },
    { label: "Relances prévues (7 j)", value: summary.relances_semaine, icon: Mail },
    { label: "Réponses reçues", value: summary.reponses_today, icon: CheckCircle2 },
    { label: "Prospects chauds", value: summary.prospects_chauds, icon: Flame },
    { label: "Emails envoyés aujourd'hui", value: summary.emails_sent_today, icon: Mail },
    { label: "Appels IA ce mois", value: summary.appels_mois ?? 0, icon: Sparkles },
    { label: "Coût IA estimé (mois)", value: `${(summary.cout_estime ?? 0).toFixed(2)} €`, icon: Sparkles },
  ] : [];

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 w-full max-w-[520px] bg-[#FAFAF7] border-l border-neutral-200 shadow-2xl flex flex-col" data-testid="agent-panel">
        <div className="px-5 pt-4 pb-3 border-b border-neutral-200 bg-white shrink-0 flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center"><Sparkles className="w-4.5 h-4.5 text-violet-600" /></span>
          <div className="flex-1">
            <h2 className="font-heading text-lg font-bold text-[#0F172A]">Agent IA de prospection</h2>
            <p className="text-[11px] text-neutral-400">Prépare, personnalise et suit vos séquences — vous validez chaque envoi.</p>
          </div>
          <button onClick={onClose} data-testid="agent-close" className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
          {/* Aujourd'hui */}
          <section data-testid="agent-summary">
            <h3 className="text-xs font-bold text-[#0F172A] mb-2">Aujourd'hui</h3>
            <div className="grid grid-cols-4 gap-2">
              {stats.map((s) => (
                <div key={s.label} className="bg-white border border-neutral-200 rounded-xl p-2.5" data-testid={`agent-stat-${s.label}`}>
                  <s.icon className="w-3.5 h-3.5 text-violet-500 mb-1" />
                  <p className="font-heading text-lg font-bold leading-tight">{s.value}</p>
                  <p className="text-[9px] text-neutral-400 leading-tight">{s.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* File de validation */}
          <section data-testid="agent-queue">
            <h3 className="text-xs font-bold text-[#0F172A] mb-2 flex items-center gap-2">
              À valider <span className="text-[10px] font-semibold text-neutral-400">{queue.length}</span>
            </h3>
            {queue.length === 0 && (
              <p className="text-[11px] text-neutral-400 bg-white border border-dashed border-neutral-200 rounded-xl p-4 text-center">
                Rien en attente — analysez un prospect ou lancez une séquence depuis sa fiche.
              </p>
            )}
            <div className="space-y-2.5">
              {queue.map((a) => {
                const cm = CHANNEL_META[a.channel] || CHANNEL_META.email;
                const ed = edited[a.id] || {};
                return (
                  <div key={a.id} className="bg-white border border-neutral-200 rounded-xl p-3" data-testid={`agent-queue-${a.id}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`inline-flex items-center gap-1 text-[9px] font-bold border rounded-full px-2 py-0.5 ${cm.cls}`}>
                        <cm.Icon className="w-3 h-3" /> {cm.label}
                      </span>
                      <span className="text-xs font-bold text-[#0F172A]">{a.prospect_name}</span>
                      {a.step_label && <span className="text-[9px] text-neutral-400">{a.step_label}</span>}
                      {a.angle && <span className="text-[9px] font-semibold text-violet-500" data-testid={`agent-angle-${a.id}`}>Angle : {a.angle}</span>}
                    </div>
                    {a.content ? (
                      <>
                        {a.subject !== null && a.subject !== undefined && a.channel === "email" && (
                          <Input className="h-7 text-[11px] font-semibold mb-1" value={ed.subject ?? a.subject ?? ""}
                            onChange={(e) => setEdited({ ...edited, [a.id]: { ...ed, subject: e.target.value } })}
                            data-testid={`agent-subject-${a.id}`} />
                        )}
                        <textarea rows={4} value={ed.content ?? a.content}
                          onChange={(e) => setEdited({ ...edited, [a.id]: { ...ed, content: e.target.value } })}
                          className="w-full text-[11px] text-neutral-600 border border-neutral-100 rounded-lg p-2 focus:outline-none focus:border-violet-300 resize-y"
                          data-testid={`agent-content-${a.id}`} />
                        {a.channel !== "email" && (
                          <p className="text-[9px] text-neutral-400 mt-0.5">
                            Envoi manuel : la validation crée une tâche To Do avec le message à copier — aucune automatisation LinkedIn/Instagram non autorisée.
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-[11px] text-neutral-400 italic">Contenu pas encore généré (étape programmée).</p>
                    )}
                    <div className="flex gap-1.5 mt-2">
                      {!a.content && (
                        <button onClick={() => generate(a)} disabled={busy === a.id} data-testid={`agent-generate-${a.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold bg-violet-600 text-white rounded-lg px-2.5 py-1.5 hover:bg-violet-700 disabled:opacity-50 transition-colors">
                          <Sparkles className="w-3 h-3" /> {busy === a.id ? "Génération…" : "Générer le message"}
                        </button>
                      )}
                      {a.content && (
                        <button onClick={() => validate(a)} disabled={busy === a.id} data-testid={`agent-validate-${a.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold bg-[#0B132B] text-white rounded-lg px-2.5 py-1.5 hover:bg-[#1C2A4F] disabled:opacity-50 transition-colors">
                          <CheckCircle2 className="w-3 h-3" /> {busy === a.id ? "…" : a.channel === "email" ? "Valider et envoyer" : "Valider → tâche manuelle"}
                        </button>
                      )}
                      {a.content && (
                        <button onClick={() => navigator.clipboard.writeText(a.content).then(() => toast.success("Copié"))} data-testid={`agent-copy-${a.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold border border-neutral-200 rounded-lg px-2 py-1.5 text-neutral-500 hover:border-neutral-400 transition-colors">
                          <Copy className="w-3 h-3" />
                        </button>
                      )}
                      <button onClick={() => setSchedFor(schedFor === a.id ? null : a.id)} data-testid={`agent-schedule-${a.id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold border border-neutral-200 rounded-lg px-2 py-1.5 text-neutral-500 hover:border-neutral-400 transition-colors">
                        Programmer
                      </button>
                      <button onClick={() => ignore(a)} data-testid={`agent-ignore-${a.id}`}
                        className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-neutral-400 hover:text-red-500 transition-colors">
                        <XCircle className="w-3 h-3" /> Ignorer
                      </button>
                    </div>
                    {schedFor === a.id && (
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <input type="date" data-testid={`agent-schedule-date-${a.id}`}
                          className="text-[11px] border border-neutral-200 rounded-lg px-2 py-1 focus:outline-none focus:border-violet-300"
                          onChange={async (e) => {
                            if (!e.target.value) return;
                            try {
                              await api.post(`/prospection/queue/${a.id}/schedule`, { due_date: e.target.value });
                              toast.success("Action programmée");
                              setSchedFor(null);
                              load();
                            } catch (err) { toast.error(apiError(err)); }
                          }} />
                        <span className="text-[9px] text-neutral-400">L'action quittera la file jusqu'à cette date.</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Cibles */}
          <section className="bg-white border border-neutral-200 rounded-xl p-3.5" data-testid="agent-targets">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-3.5 h-3.5 text-violet-500" />
              <h3 className="text-xs font-bold text-[#0F172A] flex-1">Cibles de prospection</h3>
              <button onClick={() => setNewTarget(newTarget ? null : { name: "", criteria: "", service: "" })}
                className="text-[10px] font-semibold text-violet-700 hover:underline inline-flex items-center gap-1" data-testid="agent-target-new">
                <Plus className="w-3 h-3" /> Ajouter
              </button>
            </div>
            {targets.map((t) => (
              <div key={t.id} className="flex items-start gap-2 py-1.5 border-b border-neutral-50 last:border-0" data-testid={`agent-target-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold text-[#0F172A]">{t.name}</p>
                  <p className="text-[10px] text-neutral-400">{t.criteria}{t.service ? ` → ${t.service}` : ""}</p>
                </div>
                <button onClick={() => delTarget(t)} className="text-neutral-300 hover:text-red-500" data-testid={`agent-target-del-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {targets.length === 0 && !newTarget && <p className="text-[10px] text-neutral-400">Ex : « Immobilier Genève », « Instituts beauté », « PME Haute-Savoie »…</p>}
            {newTarget && (
              <div className="mt-2 space-y-1.5 border-t border-neutral-100 pt-2">
                <Input className="h-7 text-xs" placeholder="Nom (ex : Immobilier Genève)" value={newTarget.name}
                  onChange={(e) => setNewTarget({ ...newTarget, name: e.target.value })} data-testid="agent-target-name" />
                <Input className="h-7 text-xs" placeholder="Critères (ex : agences, régies, présence digitale faible)" value={newTarget.criteria}
                  onChange={(e) => setNewTarget({ ...newTarget, criteria: e.target.value })} data-testid="agent-target-criteria" />
                <Input className="h-7 text-xs" placeholder="Service proposé (ex : Community + SEO local)" value={newTarget.service}
                  onChange={(e) => setNewTarget({ ...newTarget, service: e.target.value })} data-testid="agent-target-service" />
                <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white text-xs" onClick={addTarget} data-testid="agent-target-save">Enregistrer la cible</Button>
              </div>
            )}
          </section>

          {/* Configuration */}
          {config && (
            <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden" data-testid="agent-config">
              <button onClick={() => setCfgOpen((v) => !v)} data-testid="agent-config-toggle"
                className="w-full flex items-center gap-2 px-3.5 py-3 text-xs font-bold text-[#0F172A] hover:bg-neutral-50 transition-colors">
                <Settings2 className="w-3.5 h-3.5 text-violet-500" /> Configuration de l'agent
                <ChevronDown className={`w-3.5 h-3.5 ml-auto text-neutral-400 transition-transform ${cfgOpen ? "rotate-180" : ""}`} />
              </button>
              {cfgOpen && (
                <div className="px-3.5 pb-3.5 space-y-2.5 border-t border-neutral-100 pt-3">
                  <div>
                    <Label className="text-[10px]">Niveau d'autonomie</Label>
                    <select value={config.mode} onChange={(e) => setConfig({ ...config, mode: e.target.value })}
                      className="mt-1 w-full text-xs border border-neutral-200 rounded-lg px-2.5 py-2 bg-white" data-testid="agent-config-mode">
                      {MODE_LABELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px]">Limite emails / jour</Label>
                    <Input type="number" min="1" max="200" className="mt-1 h-8 text-xs w-28" value={config.email_limit_day}
                      onChange={(e) => setConfig({ ...config, email_limit_day: Number(e.target.value) })} data-testid="agent-config-limit" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Cooldown entre deux contacts (jours)</Label>
                    <Input type="number" min="0" max="30" className="mt-1 h-8 text-xs w-28" value={config.cooldown_days ?? 4}
                      onChange={(e) => setConfig({ ...config, cooldown_days: Number(e.target.value) })} data-testid="agent-config-cooldown" />
                  </div>
                  {CFG_FIELDS.map(([k, label, kind]) => (
                    <div key={k}>
                      <Label className="text-[10px]">{label}</Label>
                      {kind === "textarea" ? (
                        <textarea rows={2} value={config[k] || ""} onChange={(e) => setConfig({ ...config, [k]: e.target.value })}
                          className="mt-1 w-full text-xs border border-neutral-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-violet-300 resize-y"
                          data-testid={`agent-config-${k}`} />
                      ) : (
                        <Input className="mt-1 h-8 text-xs" value={config[k] || ""} onChange={(e) => setConfig({ ...config, [k]: e.target.value })}
                          data-testid={`agent-config-${k}`} />
                      )}
                    </div>
                  ))}
                  <Button onClick={saveConfig} className="w-full bg-violet-600 hover:bg-violet-700 text-white text-xs" data-testid="agent-config-save">
                    Enregistrer la configuration
                  </Button>
                </div>
              )}
            </section>
          )}
        </div>
      </aside>
    </>
  );
}
